import React, { FC, useCallback, useMemo } from "react"
import { Text } from "dash-styled"
import {
    APIGroundTruthModel,
    getGroundTruthID
} from "../../../../../../../../../Models/API/APIGroundTruthModel"
import { Table, TableColumn } from "resha"
import { TableHeader } from "../../../../../../../../Common/Table/TableHeader"
import { fromNow } from "../../../../../../../../../Helpers/Functions/dateFormat"
import {
    FetchState,
    getValue,
    hasValue
} from "../../../../../../../../../Models/Client/FetchState"
import { groundTruthSortOptions } from "../../../../Common/constants"
import { FilterAndSortExpression } from "../../../../../../../../../Models/Client/FilterAndSortExpression"
import { SortExpression } from "../../../../../../../../../Models/Client/SortExpression"
import { NothingMatched } from "../../../../../../../../Common/Empty/NothingMatched"
import { MainThunkDispatch } from "../../../../../../../../../Reducers"
import { useDispatch } from "react-redux"
import { useGroundTruthList } from "../../../../../../../../../Hooks/useGroundTruthList"
import { useGroundTruthTotalCount } from "../../../../../../../../../Hooks/useGroundTruthTotalCount"
import { loadMoreGroundTruthList } from "../../../../../../../../../Actions/API/AppInstance/AppInstanceBaselineDataActions"
import { push } from "connected-react-router"
import { routePaths } from "../../../../../../../../../Routes"

const getColumns = (): TableColumn<APIGroundTruthModel>[] => [
    {
        renderHeader: () => "Data point",
        render: model => {
            return (
                <Text className="m-0" weight="semibold">
                    {getGroundTruthID(model)}
                </Text>
            )
        },
        className: "text-smoke-610"
    },
    {
        renderHeader: () => "Measurements",
        render: (model: APIGroundTruthModel) => model.used,
        className: "text-grape-800"
    },
    {
        renderHeader: () => "Source",
        render: (model: APIGroundTruthModel) =>
            model.type === "OWNER" ? "Uploaded" : "Processed with super.AI",
        className: "text-grape-800"
    },
    {
        renderHeader: () => "Created",
        render: (model: APIGroundTruthModel) => fromNow(model.created),
        className: "text-grape-800"
    }
]

interface BaselineContentPoolIsolatedProps {
    totalCountFetch: FetchState<number>
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
    groundTruthListFetch: FetchState<{
        items: APIGroundTruthModel[]
        total: number
    }>
    onChangeSort: (sort: SortExpression<APIGroundTruthModel>) => void
    onClickRow: (item: APIGroundTruthModel) => void
    onLoadMore: () => Promise<void>
}

export const BaselineContentPoolIsolated: FC<BaselineContentPoolIsolatedProps> =
    ({
        totalCountFetch,
        filterAndSort,
        groundTruthListFetch,
        onClickRow,
        onChangeSort,
        onLoadMore
    }) => {
        const columns = useMemo(() => getColumns(), [])
        const filteredCount = hasValue(groundTruthListFetch)
            ? groundTruthListFetch.value.total
            : 0
        const items = hasValue(groundTruthListFetch)
            ? groundTruthListFetch.value.items
            : undefined
        const renderEmpty = () => <NothingMatched messageVariant="try" />

        return (
            <>
                <TableHeader
                    totalCount={getValue(totalCountFetch)}
                    sortOptions={{
                        fields: groundTruthSortOptions,
                        applied: filterAndSort.sort,
                        onChange: onChangeSort
                    }}
                    filterOptions={undefined}
                    extraActions={undefined}
                />
                <Table
                    columns={columns}
                    getKey={getGroundTruthID}
                    checkedItemIds={[]}
                    itemCount={filteredCount}
                    items={items}
                    className="w-full h-0 min-h-0 mt-0"
                    loadMore={onLoadMore}
                    onRowClick={onClickRow}
                    renderEmpty={renderEmpty}
                />
            </>
        )
    }

interface BaselineContentPoolProps {
    orgUsername: string
    appID: string
    qualifierID: string
    filterAndSort: FilterAndSortExpression<APIGroundTruthModel>
}

export const BaselineContentPool = ({
    orgUsername,
    appID,
    qualifierID,
    filterAndSort
}: BaselineContentPoolProps) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const groundTruthListFetch = useGroundTruthList(appID, filterAndSort)
    const totalCountFetch = useGroundTruthTotalCount(appID)

    const handleLoadMore = useCallback(
        () => dispatch(loadMoreGroundTruthList(appID)),
        [appID, dispatch]
    )
    const handleChangeSort = useCallback(
        (sort: SortExpression<APIGroundTruthModel>) => {
            dispatch(
                push(
                    routePaths.appQualifierTestContent(
                        orgUsername,
                        appID,
                        qualifierID,
                        { ...filterAndSort, sort }
                    )
                )
            )
        },
        [dispatch, orgUsername, appID, qualifierID, filterAndSort]
    )
    const handleRowClick = useCallback(
        (model: APIGroundTruthModel) =>
            dispatch(
                push(
                    routePaths.appQualifierTestContentBaselineDataDetail(
                        orgUsername,
                        appID,
                        qualifierID,
                        getGroundTruthID(model),
                        filterAndSort
                    )
                )
            ),
        [dispatch, orgUsername, appID, qualifierID, filterAndSort]
    )

    return (
        <BaselineContentPoolIsolated
            groundTruthListFetch={groundTruthListFetch}
            totalCountFetch={totalCountFetch}
            filterAndSort={filterAndSort}
            onChangeSort={handleChangeSort}
            onClickRow={handleRowClick}
            onLoadMore={handleLoadMore}
        />
    )
}
